import React, { useState } from 'react';
import { useQuery } from 'react-query';
import classNames from 'classnames';
import { useGate } from 'effector-react';
import { API_DOMAIN } from '@constants';
import type { LibItemSysAudio } from '@types';
import ItemList from '@components/ItemList';
import axios from '@utils/axios';
import LibModalList from '../layouts/LibModalList';
import LibModalListBody from '../layouts/LibModalListBody';
import RowSysAudio from './RowSysAudio';
import { LibItemsGate } from '../stores/libState';
import { $libSelectedApi } from '../stores/libSelected';

type TagItem<T extends string | number> = { id: T; name: string };
type FilterType = 'all' | 'free' | 'vip';
const filterItems: TagItem<FilterType>[] = [
  { id: 'all', name: '全部' },
  { id: 'free', name: '免费' },
  { id: 'vip', name: '会员免费' },
];

function ListSysAudios() {
  const tagsQuery = useQuery(
    'libSysAudioTag',
    () => {
      return axios.get<TagItem<number>[]>(
        `${API_DOMAIN}/member/my/system/music/tags`
      );
    },
    {
      staleTime: Number.POSITIVE_INFINITY,
      onSuccess: (data) => {
        if (data[0]) setTagId(data[0].id);
      },
    }
  );

  const { data: tags = [] } = tagsQuery;

  const [tagId, setTagId] = useState<number | null>(tags[0]?.id ?? null);
  const [filter, setFilter] = useState<FilterType>('all');

  const listQuery = useQuery(
    ['libSysAudioItems', tagId, filter],
    () => {
      return axios.get<LibItemSysAudio[]>(
        `${API_DOMAIN}/member/my/system/music`,
        {
          params: {
            tagId,
            ...(filter === 'free' && { free: 1 }),
            ...(filter === 'vip' && { free: 0 }),
          },
        }
      );
    },
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: tagId !== null,
    }
  );

  const { data: items = [] } = listQuery;

  useGate(LibItemsGate, items);

  if (!tagId) return null;

  return (
    <LibModalList>
      <div className="bg-white-light px-15 pt-15">
        <TagGroup
          title="类别"
          items={tags}
          activeId={tagId}
          onClick={(id) => {
            $libSelectedApi.clear();
            setTagId(id);
          }}
        />
        <TagGroup
          title="价格"
          items={filterItems}
          activeId={filter}
          onClick={(id) => {
            $libSelectedApi.clear();
            setFilter(id);
          }}
        />
      </div>
      <LibModalListBody>
        <ItemList
          itemCount={items.length}
          itemData={{ items }}
          loading={listQuery.isLoading}
          row={RowSysAudio}
        />
      </LibModalListBody>
    </LibModalList>
  );
}

function TagGroup<Item extends TagItem<string | number>>({
  title,
  items,
  activeId,
  onClick,
}: {
  title: string;
  items: Item[];
  activeId: Item['id'];
  onClick: (id: Item['id']) => void;
}) {
  return (
    <div className="mb-10 flex font-medium">
      <div className="flex-shrink-0">{`${title}：`}</div>
      <div className="ml-10 flex flex-wrap items-center text-black-gray">
        {items.map((x) => (
          <div
            key={x.id}
            className={classNames('mr-15 mb-5 cursor-pointer hover:text-blue', {
              'text-blue': activeId === x.id,
            })}
            onClick={() => {
              if (x.id !== activeId) onClick(x.id);
            }}
          >
            {x.name}
          </div>
        ))}
      </div>
    </div>
  );
}

export default ListSysAudios;
